import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useEffect, useState } from "react";
import { getPrimaryCategory, SearchableDashboard } from "../../../constants";

interface Props {
  dashboards: SearchableDashboard[];
  selectedDashboardIds: string[];
  onAdd: (dashboardId: string) => void;
  onRemove: (dashboardId: string) => void;
  onApplyAllFilters: () => void;
  onDownloadAllCsvs: () => void;
  // True only once EVERY currently-selected dashboard has confirmed-applied filters, not just any
  // one of them - see AnalyticsPage.tsx's own allFiltersApplied comment.
  allFiltersApplied: boolean;
  // False while any selected dashboard's filters response is still loading, OR if it failed
  // outright (e.g. a 404) - either way there's nothing real yet for Apply all filters to act on.
  allSelectedFiltersLoaded: boolean;
  // False once every currently-selected dashboard is access-denied - Apply all filters has nothing
  // valid to do at all then (each one's own tile already hides its own Apply Filters/Reset - see
  // ApplyFiltersTile.tsx), so it's hidden entirely here too rather than left enabled as a
  // guaranteed no-op click.
  hasAccessibleSelectedDashboard: boolean;
  // True while any selected dashboard's own CSV export is still in flight - same "something is
  // happening" gap this closes on each individual Download CSV button (ApplyFiltersTile.tsx),
  // just for the bulk one.
  isDownloadingAnyCsv: boolean;
}

export const SelectDashboardTile = (props: Props) => {
  const {
    dashboards,
    selectedDashboardIds,
    onAdd,
    onRemove,
    onApplyAllFilters,
    onDownloadAllCsvs,
    allFiltersApplied,
    allSelectedFiltersLoaded,
    hasAccessibleSelectedDashboard,
    isDownloadingAnyCsv,
  } = props;

  // Only offer dashboards not already selected - picking one already in the list would just be a
  // confusing no-op via the Add button below.
  const availableDashboards = dashboards.filter((dashboard) => !selectedDashboardIds.includes(dashboard.key));
  // Starts unselected (not auto-picking the first available dashboard) so the dropdown shows its
  // "Select a dashboard..." placeholder option until the user actually picks one.
  const [pendingDashboardId, setPendingDashboardId] = useState("");

  // Clears the pending selection if it stops being available (e.g. it just got added) rather than
  // pointing at a now-hidden <option> - back to the placeholder, not auto-selecting another one.
  useEffect(() => {
    if (pendingDashboardId && !availableDashboards.some((dashboard) => dashboard.key === pendingDashboardId)) {
      setPendingDashboardId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDashboards]);

  const selectedDashboards = selectedDashboardIds
    .map((dashboardId) => dashboards.find((dashboard) => dashboard.key === dashboardId))
    .filter((dashboard): dashboard is SearchableDashboard => Boolean(dashboard));

  const handleAdd = () => {
    if (!pendingDashboardId) return;
    onAdd(pendingDashboardId);
  };

  return (
    <div className="analysis_tile" data-test-id="analysis-tile-select-dashboard">
      <div className="analysis_tile_header_row">
        <h2 className="analysis_tile_title">ADD A DASHBOARD</h2>
      </div>

      <div className="analysis_tile_dashboard_picker">
        <select
          className="analysis_tile_select"
          value={pendingDashboardId}
          onChange={(e) => setPendingDashboardId(e.target.value)}
          disabled={availableDashboards.length === 0}
          data-test-id="analysis-dashboard-select"
        >
          {availableDashboards.length === 0 ? (
            <option value="">All dashboards added</option>
          ) : (
            <>
              <option value="" disabled>
                Select a dashboard...
              </option>
              {availableDashboards.map((dashboard) => (
                <option key={dashboard.key} value={dashboard.key}>
                  {dashboard.name} - {getPrimaryCategory(dashboard)}
                </option>
              ))}
            </>
          )}
        </select>
        <button
          className="analysis_button_primary analysis_dashboard_add_button"
          onClick={handleAdd}
          disabled={!pendingDashboardId}
          data-test-id="analysis-dashboard-add"
        >
          Add
        </button>

        {selectedDashboards.length > 0 && (
          <div className="analysis_tile_bulk_actions">
            {/* Hidden entirely (not just disabled) once every selected dashboard is access-denied -
                there's nothing valid left for it to act on then, same reasoning as Apply Filters/
                Reset already being hidden per-dashboard (ApplyFiltersTile.tsx) rather than shown
                disabled. */}
            {hasAccessibleSelectedDashboard && (
              <button
                className="analysis_tile_reset_button"
                onClick={onApplyAllFilters}
                disabled={!allSelectedFiltersLoaded}
                title={allSelectedFiltersLoaded ? undefined : "Still loading filters for every selected dashboard"}
                data-test-id="analysis-apply-all-filters"
              >
                Apply all filters
              </button>
            )}
            {/* Hidden entirely (not just disabled) until every selected (accessible) dashboard has
                confirmed-applied filters - a grayed-out button here had nothing valid to export
                against yet regardless, so there was nothing meaningful for "disabled" to
                communicate beyond what its own absence already says on its own. */}
            {allFiltersApplied && (
              <button
                className="analysis_button_primary"
                onClick={onDownloadAllCsvs}
                disabled={isDownloadingAnyCsv}
                data-test-id="analysis-download-all-csvs"
              >
                {isDownloadingAnyCsv ? (
                  <CircularProgress size={16} color="inherit" data-test-id="analysis-download-all-csvs-spinner" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )}
                {isDownloadingAnyCsv ? "Downloading…" : "Download all CSVs"}
              </button>
            )}
          </div>
        )}
      </div>

      {selectedDashboards.length > 0 && (
        <div className="analysis_dashboard_pills">
          {selectedDashboards.map((dashboard) => (
            <span className="analysis_dashboard_pill" key={dashboard.key} data-test-id={`analysis-dashboard-pill-${dashboard.key}`}>
              {dashboard.name}
              <button
                className="analysis_dashboard_pill_remove"
                onClick={() => onRemove(dashboard.key)}
                aria-label={`Remove ${dashboard.name}`}
                data-test-id={`analysis-dashboard-pill-remove-${dashboard.key}`}
              >
                <CloseIcon fontSize="inherit" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
